import { useCustomToast } from '@/hooks/use-custom-toast';
import { memo, useCallback, useMemo } from 'react';
import { queryClient } from '@/lib/react-query';
import { axiosExtractErrorMessage } from '@/lib/axios';
import { useChannelModalRootActionsStore } from '@/features/channels/_shared/components/channel-modal-root/channel-modal-root.store';
import ModalWithConfirmation from '@/components/_ui/modal-with-confirmation/modal-with-confirmation';
import { useDeleteNolimitEngageIntegration } from '../../api/delete-integration-nolimit-engage.api';
import { GET_LIST_INTEGRATION_NOLIMIT_ENGAGE_QUERY_KEY } from '../../api/get-list-integration-nolimit-engage.api';

const TOAST_MESSAGES_DELETE_NOLIMIT_ENGAGE = {
  success: 'The channel integration has been successfully deleted!',
  error: 'Failed to delete Nolimit Engage integration!',
};

export type TDeleteNolimitEngageIntegrationModalType = 'DeleteNolimitEngageIntegrationModal';
export interface IDeleteNolimitEngageIntegrationModalProps {
  organizationChannelId: string;
}

const DeleteNolimitEngageIntegrationModal = memo(
  ({ organizationChannelId }: IDeleteNolimitEngageIntegrationModalProps) => {
    const showToast = useCustomToast();
    const channelModalRootActions = useChannelModalRootActionsStore();

    const isOpenDeleteNolimitEngageIntegrationModal = useMemo(() => {
      return channelModalRootActions.checkIsOpenModal('DeleteNolimitEngageIntegrationModal');
    }, [channelModalRootActions]);

    const mutationDeleteIntegrationNolimitEngage = useDeleteNolimitEngageIntegration({
      mutationConfig: {
        onError: (error) => {
          const errorMessage = axiosExtractErrorMessage(error) || TOAST_MESSAGES_DELETE_NOLIMIT_ENGAGE.error;
          showToast({ type: 'error', message: errorMessage });
        },
        onSuccess: () => {
          Promise.all([queryClient.invalidateQueries({ queryKey: [GET_LIST_INTEGRATION_NOLIMIT_ENGAGE_QUERY_KEY] })]);
          channelModalRootActions.closeModal('DeleteNolimitEngageIntegrationModal');
          showToast({ type: 'success', message: TOAST_MESSAGES_DELETE_NOLIMIT_ENGAGE.success });
        },
      },
    });

    const handleDelete = useCallback(() => {
      mutationDeleteIntegrationNolimitEngage.mutate({
        organizationChannelId: organizationChannelId || '',
      });
    }, [mutationDeleteIntegrationNolimitEngage, organizationChannelId]);

    const handleClose = useCallback(() => {
      channelModalRootActions.closeModal('DeleteNolimitEngageIntegrationModal');
    }, [channelModalRootActions]);

    return (
      <ModalWithConfirmation
        isOpen={isOpenDeleteNolimitEngageIntegrationModal}
        onClose={handleClose}
        onDelete={handleDelete}
        isLoading={mutationDeleteIntegrationNolimitEngage.isPending}
        title="Delete Nolimit Engage Integration"
        description="Do you want to delete this integration?"
        bannerTitle="Deleting this channel will permanently delete all related data, including chat history and analytics."
        confirmText="delete my channel"
      />
    );
  }
);

DeleteNolimitEngageIntegrationModal.displayName = 'DeleteNolimitEngageIntegrationModal';

export default DeleteNolimitEngageIntegrationModal;
